import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { studentsAPI, feeTemplatesAPI, hostelFeeTemplatesAPI, transportFeeTemplatesAPI } from '../services/api';
import { useYear } from '../context/YearContext';

const today = () => new Date().toISOString().split('T')[0];

// HTML date inputs only display values in YYYY-MM-DD format.
const toDateInputValue = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    const isoDate = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoDate) return isoDate[1];
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().split('T')[0];
};

const FEE_TYPES = ['Regular', 'Olympiad', 'IIT'];
const makeFeeKey = (cls, feeType) => `${(cls || '').trim()}|${feeType}`;

const makeEmpty = (year) => ({
  student_name:      '',
  father_name:       '',
  mother_name:       '',
  mobile_number:     '',
  class:             '',
  fee_type:          'Regular',
  village:           '',
  admission_date:    today(),
  total_fee:         '',
  paid_amount:       '',
  old_due_amount:    '',
  concession_amount: '',
  concession_reason: '',
  academic_year:     year,
  admission_no:      '',
  notes:             '',
  gender:            '',
  dob:               '',
  caste:             '',
  pen_number:        '',
  bus_fee:              '',
  bus_fee_term1:        '',
  bus_fee_term2:        '',
  tuition_fee_term1:    '',
  tuition_fee_term2:    '',
  tuition_fee_term3:    '',
  uniform_fee:          '',
  books_fee:         '',
  aadhar_id:         '',
});

export default function StudentForm({ student, schoolName, onClose, onSave }) {
  const { year }              = useYear();
  const [form,       setForm]       = useState(() => makeEmpty(year));
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState(null);
  const [dupWarning, setDupWarning] = useState(null);
  const [feeTemplates,       setFeeTemplates]       = useState({});
  const [preferBus,          setPreferBus]          = useState('no');
  const preferBusRef                                 = useRef('no');
  const [preferHostel,       setPreferHostel]       = useState('no');
  const [hostelType,          setHostelType]          = useState('');
  const [hostelFeeTemplate,  setHostelFeeTemplate]  = useState({ ac_room: 0, non_ac_room: 0, mess: 0, laundry: 0, ac_term1: 0, ac_term2: 0, non_ac_term1: 0, non_ac_term2: 0 });
  const [transportTemplates, setTransportTemplates] = useState([]); // [{ village_name, fee_term1, fee_term2 }]

  // Load fee templates from API for the current academic year
  useEffect(() => {
    feeTemplatesAPI.getAll(year)
      .then((r) => setFeeTemplates(r.data.data || {}))
      .catch(() => {});
  }, [year]);

  // Load hostel fee template
  useEffect(() => {
    hostelFeeTemplatesAPI.getAll(year)
      .then((r) => {
        const rows = r.data.data || [];
        const nameToKey = {
          'ac hostel':     'ac_room',
          'non ac hostel': 'non_ac_room',
          'ac room':       'ac_room',
          'non-ac room':   'non_ac_room',
          'non ac room':   'non_ac_room',
          'mess':          'mess',
          'laundry':       'laundry',
        };
        const template = { ac_room: 0, non_ac_room: 0, mess: 0, laundry: 0, ac_term1: 0, ac_term2: 0, non_ac_term1: 0, non_ac_term2: 0 };
        rows.forEach((row) => {
          const key = nameToKey[(row.fee_name || '').toLowerCase().trim()];
          if (key) {
            template[key] = parseFloat(row.fee_amount) || 0;
            if (key === 'ac_room') {
              template.ac_term1 = parseFloat(row.fee_term1) || 0;
              template.ac_term2 = parseFloat(row.fee_term2) || 0;
            } else if (key === 'non_ac_room') {
              template.non_ac_term1 = parseFloat(row.fee_term1) || 0;
              template.non_ac_term2 = parseFloat(row.fee_term2) || 0;
            }
          }
        });
        setHostelFeeTemplate(template);
      })
      .catch(() => {});
  }, [year]);

  // Load transport fee templates for village dropdown + auto-fill
  useEffect(() => {
    transportFeeTemplatesAPI.getAll(year)
      .then((r) => setTransportTemplates(r.data.data || []))
      .catch(() => {});
  }, [year]);

  useEffect(() => {
    if (student) {
      setForm({
        student_name:      student.student_name      || '',
        father_name:       student.father_name       || '',
        mother_name:       student.mother_name       || '',
        mobile_number:     student.mobile_number     || '',
        class:             student.class             || '',
        fee_type:          student.fee_type          || 'Regular',
        village:           student.village           || '',
        admission_date:    toDateInputValue(student.admission_date) || today(),
        total_fee:         student.total_fee         !== undefined ? student.total_fee         : '',
        paid_amount:       student.paid_amount       !== undefined ? student.paid_amount       : '',
        old_due_amount:    student.old_due_amount    !== undefined ? student.old_due_amount    : '',
        concession_amount: student.concession_amount !== undefined ? student.concession_amount : '',
        concession_reason: student.concession_reason || '',
        academic_year:     student.academic_year     || year,
        admission_no:      student.admission_no      || '',
        notes:             student.notes             || '',
        gender:            student.gender            || '',
        dob:               toDateInputValue(student.dob),
        caste:             student.caste             || '',
        pen_number:        student.pen_number        || '',
        bus_fee:           student.bus_fee           !== undefined ? student.bus_fee           : '',
        bus_fee_term1:        student.bus_fee_term1         !== undefined ? student.bus_fee_term1         : '',
        bus_fee_term2:        student.bus_fee_term2         !== undefined ? student.bus_fee_term2         : '',
        tuition_fee_term1:    student.tuition_fee_term1     !== undefined ? student.tuition_fee_term1     : '',
        tuition_fee_term2:    student.tuition_fee_term2     !== undefined ? student.tuition_fee_term2     : '',
        tuition_fee_term3:    student.tuition_fee_term3     !== undefined ? student.tuition_fee_term3     : '',
        uniform_fee:       student.uniform_fee       !== undefined ? student.uniform_fee       : '',
        books_fee:         student.books_fee         !== undefined ? student.books_fee         : '',
        aadhar_id:         student.aadhar_id         || '',
      });
      const hasBus = parseFloat(student.bus_fee) > 0;
      preferBusRef.current = hasBus ? 'yes' : 'no';
      setPreferBus(hasBus ? 'yes' : 'no');

      const storedTypes = (student.hostel_types || '').split(',').filter(Boolean);
      const detectedType = storedTypes.includes('ac_room') ? 'ac' : storedTypes.includes('non_ac_room') ? 'non_ac' : '';
      setHostelType(detectedType);
      setPreferHostel((storedTypes.length > 0 || parseFloat(student.hostel_fee) > 0) ? 'yes' : 'no');
    }
  }, [student, year]);

  const applyFeeTemplate = (templates, cls, feeType, prev, preferBus) => {
    // Try new-style key "Class|FeeType" first, fall back to old-style "Class"
    const newKey = makeFeeKey(cls, feeType);
    const t = templates?.[newKey] ?? templates?.[cls.trim()];
    if (!t) return prev;
    // For new-style templates: tuition=term1, bus=term2, uniform=uniform, books=books
    const isNewStyle = !!templates?.[newKey];
    if (isNewStyle) {
      const term1   = t.tuition || 0;
      const term2   = t.bus     || 0;
      const term3   = t.term3   || 0;
      const uniform = t.uniform || 0;
      const books   = t.books   || 0;
      return {
        ...prev,
        total_fee:         String(term1 + term2 + term3),
        tuition_fee_term1: term1 > 0 ? String(term1) : '',
        tuition_fee_term2: term2 > 0 ? String(term2) : '',
        tuition_fee_term3: term3 > 0 ? String(term3) : '',
        uniform_fee:       uniform > 0 ? String(uniform) : '',
        books_fee:         books   > 0 ? String(books)   : '',
        bus_fee:           '',
      };
    }
    // Old-style template — respect bus opt-in; bus maps to term1 (no term2/3 in old templates)
    return {
      ...prev,
      total_fee:         t.tuition > 0 ? String(t.tuition) : '',
      tuition_fee_term1: '',
      tuition_fee_term2: '',
      tuition_fee_term3: '',
      bus_fee_term1:     (preferBus === 'yes' && t.bus > 0) ? String(t.bus) : '',
      bus_fee_term2:     '',
      bus_fee:           '',
      uniform_fee:       t.uniform > 0 ? String(t.uniform) : '',
      books_fee:         t.books   > 0 ? String(t.books)   : '',
    };
  };

  const applyTransportTemplate = (formState, villageName) => {
    if (preferBusRef.current !== 'yes') return formState;
    const tpl = transportTemplates.find((t) => t.village_name === villageName);
    if (!tpl) return formState;
    return {
      ...formState,
      bus_fee_term1: tpl.fee_term1 > 0 ? String(tpl.fee_term1) : '',
      bus_fee_term2: tpl.fee_term2 > 0 ? String(tpl.fee_term2) : '',
      bus_fee:       '',
    };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === 'class' && value) {
        return applyFeeTemplate(feeTemplates, value, prev.fee_type, updated, preferBusRef.current);
      }
      if (name === 'fee_type' && prev.class) {
        return applyFeeTemplate(feeTemplates, prev.class, value, updated, preferBusRef.current);
      }
      if (name === 'village') {
        return applyTransportTemplate(updated, value);
      }
      return updated;
    });
  };

  // Duplicate check when both name fields are filled
  const checkDuplicate = useCallback(async () => {
    if (!form.student_name.trim() || !form.father_name.trim()) return;
    try {
      const res = await studentsAPI.checkDuplicate(
        form.student_name.trim(),
        form.father_name.trim(),
        student?.student_id
      );
      if (res.data.data.exists) {
        const m = res.data.data.match;
        setDupWarning(`A student named "${m.student_name}" with same father already exists in ${m.class} (${m.academic_year}).`);
      } else {
        setDupWarning(null);
      }
    } catch { /* non-critical */ }
  }, [form.student_name, form.father_name, student]);

  const handlePreferBusChange = (val) => {
    preferBusRef.current = val;
    setPreferBus(val);
    if (val === 'no') {
      setForm(f => ({ ...f, bus_fee: '', bus_fee_term1: '', bus_fee_term2: '' }));
    } else {
      // Auto-fill from transport fee template for the currently selected village
      setForm(f => {
        const tpl = transportTemplates.find((t) => t.village_name === f.village);
        if (tpl) {
          return {
            ...f,
            bus_fee_term1: tpl.fee_term1 > 0 ? String(tpl.fee_term1) : '',
            bus_fee_term2: tpl.fee_term2 > 0 ? String(tpl.fee_term2) : '',
            bus_fee:       '',
          };
        }
        return f;
      });
    }
  };

  const handlePreferHostelChange = (val) => {
    setPreferHostel(val);
    if (val === 'no') {
      setHostelType('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const motherName = form.mother_name.trim();

    setLoading(true);
    try {
      const t1 = parseFloat(form.bus_fee_term1) || 0;
      const t2 = parseFloat(form.bus_fee_term2) || 0;
      const tt1 = parseFloat(form.tuition_fee_term1) || 0;
      const tt2 = parseFloat(form.tuition_fee_term2) || 0;
      const tt3 = parseFloat(form.tuition_fee_term3) || 0;
      const hostelTerm1 = preferHostel === 'yes'
        ? (hostelType === 'ac' ? (hostelFeeTemplate.ac_term1 || 0) : hostelType === 'non_ac' ? (hostelFeeTemplate.non_ac_term1 || 0) : 0)
        : 0;
      const hostelTerm2 = preferHostel === 'yes'
        ? (hostelType === 'ac' ? (hostelFeeTemplate.ac_term2 || 0) : hostelType === 'non_ac' ? (hostelFeeTemplate.non_ac_term2 || 0) : 0)
        : 0;
      const hostelTotal = hostelTerm1 + hostelTerm2;
      const payload = {
        ...form,
        mother_name:           motherName,
        bus_fee_term1:         t1,
        bus_fee_term2:         t2,
        bus_fee:               t1 + t2,
        tuition_fee_term1:     tt1,
        tuition_fee_term2:     tt2,
        tuition_fee_term3:     tt3,
        total_fee:             (tt1 + tt2 + tt3) > 0 ? tt1 + tt2 + tt3 : parseFloat(form.total_fee) || 0,
        hostel_fee:            hostelTotal,
        hostel_fee_term1:      hostelTerm1,
        hostel_fee_term2:      hostelTerm2,
        hostel_types:          preferHostel === 'yes' && hostelType ? (hostelType === 'ac' ? 'ac_room' : 'non_ac_room') : '',
      };
      if (student) {
        await studentsAPI.update(student.student_id, payload);
      } else {
        await studentsAPI.create(payload);
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save student. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const totalFee        = parseFloat(form.total_fee)           || 0;
  const paidAmount      = parseFloat(form.paid_amount)         || 0;
  const oldDue          = parseFloat(form.old_due_amount)      || 0;
  const concession      = parseFloat(form.concession_amount)   || 0;
  const busTerm1        = parseFloat(form.bus_fee_term1)       || 0;
  const busTerm2        = parseFloat(form.bus_fee_term2)       || 0;
  const busFeeTotal     = busTerm1 + busTerm2;
  const tuitionTerm1    = parseFloat(form.tuition_fee_term1)   || 0;
  const tuitionTerm2    = parseFloat(form.tuition_fee_term2)   || 0;
  const tuitionTerm3    = parseFloat(form.tuition_fee_term3)   || 0;
  const feeFromTemplate = !!(feeTemplates?.[makeFeeKey(form.class || '', form.fee_type || 'Regular')] ?? feeTemplates?.[form.class?.trim()]);
  const uniformFee      = parseFloat(form.uniform_fee)         || 0;
  const booksFee        = parseFloat(form.books_fee)           || 0;
  const hostelFeeTotal  = preferHostel === 'yes'
    ? (hostelType === 'ac' ? (hostelFeeTemplate.ac_room || 0) : hostelType === 'non_ac' ? (hostelFeeTemplate.non_ac_room || 0) : 0)
    : 0;
  const pendingAmount   = totalFee + busFeeTotal + uniformFee + booksFee + hostelFeeTotal - paidAmount + oldDue - concession;

  return createPortal(
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-box large">
        {/* Header */}
        <div className="modal-header">
          <h2>{student ? 'Edit Student' : 'Add New Student'}</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="error-banner">{error}</div>}
            {dupWarning && <div className="dup-warning-banner">⚠️ {dupWarning}</div>}

            <div className="form-grid">
              {schoolName && (
                <div className="form-group full-width">
                  <label>School</label>
                  <input type="text" value={schoolName} readOnly disabled style={{background:'#f3f4f6',cursor:'default',fontWeight:600}} />
                </div>
              )}

              <div className="form-group">
                <label>Student Name <span className="required">*</span></label>
                <input
                  type="text"
                  name="student_name"
                  value={form.student_name}
                  onChange={handleChange}
                  onBlur={!student ? checkDuplicate : undefined}
                  required
                  placeholder="Full name"
                  maxLength={200}
                />
              </div>
              <div className="form-group">
                <label>Admission No <span className="required">*</span></label>
                <input
                  type="text"
                  name="admission_no"
                  value={form.admission_no}
                  onChange={handleChange}
                  required
                  placeholder="e.g. ADM/2025/001"
                  maxLength={50}
                />
              </div>
              <div className="form-group">
                <label>Fee Type</label>
                <select
                  name="fee_type"
                  value={form.fee_type}
                  onChange={handleChange}
                >
                  {FEE_TYPES.map((ft) => (
                    <option key={ft} value={ft}>{ft}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Class <span className="required">*</span></label>
                <input
                  type="text"
                  name="class"
                  value={form.class}
                  onChange={handleChange}
                  required
                  placeholder="e.g. Class 5, LKG, 10th"
                  maxLength={50}
                />
              </div>
              <div className="form-group">
                <label>Gender {!(student && year === '2025-26') && <span className="required">*</span>}</label>
                <select
                  name="gender"
                  value={form.gender}
                  onChange={handleChange}
                  required={!(student && year === '2025-26')}
                >
                  <option value="">-- Select --</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>
              <div className="form-group">
                  <label>Date of Birth {!(student && year === '2025-26') && <span className="required">*</span>}</label>
                  <input
                   type="date"
                  name="dob"
                   value={form.dob}
                  onChange={handleChange}
                  required={!(student && year === '2025-26')}
                  lang="en-GB"
                 />
              </div>
              <div className="form-group">
                <label>Father Name <span className="required">*</span></label>
                <input
                  type="text"
                  name="father_name"
                  value={form.father_name}
                  onChange={handleChange}
                  onBlur={!student ? checkDuplicate : undefined}
                  required
                  placeholder="Father's full name"
                  maxLength={200}
                />
              </div>

              <div className="form-group">
                <label>Mother Name {!(student && year === '2025-26') && <span className="required">*</span>}</label>
                <input
                  type="text"
                  name="mother_name"
                  value={form.mother_name}
                  onChange={handleChange}
                  required={!(student && year === '2025-26')}
                  placeholder="Mother's full name"
                  maxLength={200}
                />
              </div>

              <div className="form-group">
                <label>Mobile Number <span className="required">*</span></label>
                <input
                  type="tel"
                  name="mobile_number"
                  value={form.mobile_number}
                  onChange={handleChange}
                  required
                  placeholder="10-digit mobile number"
                  maxLength={15}
                  pattern="[0-9]{10,15}"
                  title="Enter a valid 10–15 digit mobile number"
                />
              </div>

              <div className="form-group">
                <label>Village {!(student && year === '2025-26') && <span className="required">*</span>}</label>
                <select
                  name="village"
                  value={form.village}
                  onChange={handleChange}
                  required={!(student && year === '2025-26')}
                >
                  <option value="">-- Select Village --</option>
                  {transportTemplates.map((t) => (
                    <option key={t.village_name} value={t.village_name}>{t.village_name}</option>
                  ))}
                  {/* Keep the student's existing village even if template was removed */}
                  {form.village && !transportTemplates.find((t) => t.village_name === form.village) && (
                    <option value={form.village}>{form.village}</option>
                  )}
                </select>
              </div>

              <div className="form-group">
                <label>Admission Date <span className="required">*</span></label>
                <input
                  type="date"
                  name="admission_date"
                  value={form.admission_date}
                  onChange={handleChange}
                  required
                  lang="en-GB"
                />
              </div>

              <div className="form-group">
                <label>Caste</label>
                <select
                  name="caste"
                  value={form.caste}
                  onChange={handleChange}
                >
                  <option value="">-- Select --</option>
                  <option value="OC/General">OC/General</option>
                  <option value="BC">BC</option>
                  <option value="OBC">OBC</option>
                  <option value="ST">ST</option>
                  <option value="SC">SC</option>
                  <option value="Others">Others</option>
                </select>
              </div>

              <div className="form-group">
                <label>Aadhaar ID</label>
                <input
                  type="text"
                  name="aadhar_id"
                  value={form.aadhar_id}
                  onChange={handleChange}
                  placeholder="12-digit Aadhaar number"
                  maxLength={12}
                  pattern="[0-9]{0,12}"
                  title="Enter a valid 12-digit Aadhaar number"
                />
              </div>

              <div className="form-group">
                <label>PEN Number</label>
                <input
                  type="text"
                  name="pen_number"
                  value={form.pen_number}
                  onChange={handleChange}
                  placeholder="Enter PEN number"
                />
              </div>

              <div className="form-group">
                <label>Tuition Fee (₹){(tuitionTerm1 + tuitionTerm2 + tuitionTerm3) > 0 ? ' — auto' : ''}</label>
                <input
                  type="number"
                  name="total_fee"
                  value={(tuitionTerm1 + tuitionTerm2 + tuitionTerm3) > 0 ? (tuitionTerm1 + tuitionTerm2 + tuitionTerm3).toFixed(2) : form.total_fee}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  placeholder="0"
                  readOnly={(tuitionTerm1 + tuitionTerm2 + tuitionTerm3) > 0}
                  className={(tuitionTerm1 + tuitionTerm2 + tuitionTerm3) > 0 ? 'readonly-input' : ''}
                />
              </div>

              <div className="form-group">
                <label>Tuition Fee – Term 1 (₹)</label>
                <input
                  type="number"
                  name="tuition_fee_term1"
                  value={form.tuition_fee_term1}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  placeholder="0"
                  readOnly={feeFromTemplate}
                  className={feeFromTemplate ? 'readonly-input' : ''}
                />
              </div>

              <div className="form-group">
                <label>Tuition Fee – Term 2 (₹)</label>
                <input
                  type="number"
                  name="tuition_fee_term2"
                  value={form.tuition_fee_term2}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  placeholder="0"
                  readOnly={feeFromTemplate}
                  className={feeFromTemplate ? 'readonly-input' : ''}
                />
              </div>

              <div className="form-group">
                <label>Tuition Fee – Term 3 (₹)</label>
                <input
                  type="number"
                  name="tuition_fee_term3"
                  value={form.tuition_fee_term3}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  placeholder="0"
                  readOnly={feeFromTemplate}
                  className={feeFromTemplate ? 'readonly-input' : ''}
                />
              </div>

              <div className="form-group">
                <label>Paid Amount (₹)</label>
                <input
                  type="number"
                  name="paid_amount"
                  value={form.paid_amount}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  placeholder="0"
                />
              </div>

              <div className="form-group">
                <label>Old Due Amount (₹)</label>
                <input
                  type="number"
                  name="old_due_amount"
                  value={form.old_due_amount}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  placeholder="0"
                />
              </div>

              <div className="form-group">
                <label>Concession / Scholarship (₹)</label>
                <input
                  type="number"
                  name="concession_amount"
                  value={form.concession_amount}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  placeholder="0"
                />
              </div>

              <div className="form-group">
                <label>Concession Reason</label>
                <input
                  type="text"
                  name="concession_reason"
                  value={form.concession_reason}
                  onChange={handleChange}
                  placeholder="e.g. Merit scholarship, Staff ward"
                  maxLength={500}
                />
              </div>

              <div className="form-group">
                <label style={preferHostel === 'yes' ? { color: '#94a3b8' } : undefined}>Prefer School Bus</label>
                <select
                  value={preferBus}
                  onChange={e => handlePreferBusChange(e.target.value)}
                  disabled={preferHostel === 'yes'}
                  style={preferHostel === 'yes' ? { background: '#f1f5f9', color: '#94a3b8', cursor: 'not-allowed', opacity: 0.6 } : undefined}
                  title={preferHostel === 'yes' ? 'Not available when Hostel is selected' : undefined}
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </div>

              {preferBus === 'yes' && (
                <>
                  <div className="form-group">
                    <label>Transport Fee – Term 1 (₹)</label>
                    <input
                      type="number"
                      name="bus_fee_term1"
                      value={form.bus_fee_term1}
                      onChange={handleChange}
                      min="0"
                      step="0.01"
                      placeholder="0"
                      readOnly={busTerm1 > 0}
                      className={busTerm1 > 0 ? 'readonly-input' : ''}
                    />
                  </div>
                  <div className="form-group">
                    <label>Transport Fee – Term 2 (₹)</label>
                    <input
                      type="number"
                      name="bus_fee_term2"
                      value={form.bus_fee_term2}
                      onChange={handleChange}
                      min="0"
                      step="0.01"
                      placeholder="0"
                      readOnly={busTerm2 > 0}
                      className={busTerm2 > 0 ? 'readonly-input' : ''}
                    />
                  </div>
                  {busFeeTotal > 0 && (
                    <div className="form-group">
                      <label>Transport Fee Total (₹) — auto</label>
                      <input
                        type="text"
                        value={busFeeTotal.toFixed(2)}
                        readOnly
                        className="readonly-input"
                      />
                    </div>
                  )}
                </>
              )}

              <div className="form-group">
                <label style={preferBus === 'yes' ? { color: '#94a3b8' } : undefined}>Prefer School Hostel</label>
                <select
                  value={preferHostel}
                  onChange={(e) => handlePreferHostelChange(e.target.value)}
                  disabled={preferBus === 'yes'}
                  style={preferBus === 'yes' ? { background: '#f1f5f9', color: '#94a3b8', cursor: 'not-allowed', opacity: 0.6 } : undefined}
                  title={preferBus === 'yes' ? 'Not available when Bus is selected' : undefined}
                >
                  <option value="no">No — Day Scholar</option>
                  <option value="yes">Yes — Hostler</option>
                </select>
              </div>

              {preferHostel === 'yes' && (
                <>
                  <div className="form-group">
                    <label>Prefer Hostel Type</label>
                    <select
                      value={hostelType}
                      onChange={(e) => setHostelType(e.target.value)}
                    >
                      <option value="">— Select —</option>
                      <option value="ac">AC Hostel</option>
                      <option value="non_ac">Non AC Hostel</option>
                    </select>
                  </div>

                  {hostelType === 'ac' && (
                    <>
                      <div className="form-group">
                        <label>Term 1 Hostel Fee (₹)</label>
                        <input
                          type="text"
                          value={hostelFeeTemplate.ac_term1 > 0 ? hostelFeeTemplate.ac_term1.toLocaleString('en-IN') : '0'}
                          readOnly
                          className="readonly-input"
                        />
                      </div>
                      <div className="form-group">
                        <label>Term 2 Hostel Fee (₹)</label>
                        <input
                          type="text"
                          value={hostelFeeTemplate.ac_term2 > 0 ? hostelFeeTemplate.ac_term2.toLocaleString('en-IN') : '0'}
                          readOnly
                          className="readonly-input"
                        />
                      </div>
                      <div className="form-group">
                        <label>Total Hostel Fee (₹)</label>
                        <input
                          type="text"
                          value={hostelFeeTemplate.ac_room > 0 ? hostelFeeTemplate.ac_room.toLocaleString('en-IN') : '0'}
                          readOnly
                          className="readonly-input"
                        />
                      </div>
                    </>
                  )}

                  {hostelType === 'non_ac' && (
                    <>
                      <div className="form-group">
                        <label>Term 1 Hostel Fee (₹)</label>
                        <input
                          type="text"
                          value={hostelFeeTemplate.non_ac_term1 > 0 ? hostelFeeTemplate.non_ac_term1.toLocaleString('en-IN') : '0'}
                          readOnly
                          className="readonly-input"
                        />
                      </div>
                      <div className="form-group">
                        <label>Term 2 Hostel Fee (₹)</label>
                        <input
                          type="text"
                          value={hostelFeeTemplate.non_ac_term2 > 0 ? hostelFeeTemplate.non_ac_term2.toLocaleString('en-IN') : '0'}
                          readOnly
                          className="readonly-input"
                        />
                      </div>
                      <div className="form-group">
                        <label>Total Hostel Fee (₹)</label>
                        <input
                          type="text"
                          value={hostelFeeTemplate.non_ac_room > 0 ? hostelFeeTemplate.non_ac_room.toLocaleString('en-IN') : '0'}
                          readOnly
                          className="readonly-input"
                        />
                      </div>
                    </>
                  )}
                </>
              )}

              <div className="form-group">
                <label>Uniform Fee (₹)</label>
                <input
                  type="number"
                  name="uniform_fee"
                  value={form.uniform_fee}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  placeholder="0 (one-time)"
                  readOnly={feeFromTemplate && uniformFee > 0}
                  className={feeFromTemplate && uniformFee > 0 ? 'readonly-input' : ''}
                />
              </div>

              <div className="form-group">
                <label>Books Fee (₹)</label>
                <input
                  type="number"
                  name="books_fee"
                  value={form.books_fee}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  placeholder="0 (one-time)"
                  readOnly={feeFromTemplate && booksFee > 0}
                  className={feeFromTemplate && booksFee > 0 ? 'readonly-input' : ''}
                />
              </div>

              <div className="form-group">
                <label>Pending Amount (₹) — AUTO{preferHostel === 'yes' && hostelFeeTotal > 0 ? ' (incl. hostel)' : ''}</label>
                <input
                  type="text"
                  value={pendingAmount.toFixed(2)}
                  readOnly
                  className={`readonly-input ${pendingAmount > 0 ? 'pending-input' : 'cleared-input'}`}
                />
              </div>
            </div>

            <div className="form-group" style={{marginTop:'10px'}}>
              <label>Notes</label>
              <textarea
                name="notes"
                value={form.notes}
                onChange={handleChange}
                rows={3}
                placeholder="Any additional notes about this student..."
                maxLength={1000}
                style={{width:'100%',resize:'vertical',padding:'8px 10px',border:'1px solid #d1d5db',borderRadius:'6px',fontSize:'0.9rem'}}
              />
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving…' : student ? 'Update Student' : 'Add Student'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
